import { useEffect, useRef, useState } from 'react';
import { placesApi, type AutocompletePrediction, type PlaceBasicDetails } from '../../api/places.api';
import Spinner from './Spinner';
import { Icon, MapPin } from './Icon';

interface Props {
  value:        string;
  onChange:     (value: string) => void;
  onSelect:     (detail: PlaceBasicDetails) => void;
  placeholder?: string;
  bias?:        string;   // "name + city" → forwarded to backend; backend can geocode if needed (currently unused as locationBias)
  className?:   string;
  autoFocus?:   boolean;
}

function genToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, className, autoFocus }: Props) {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [loading, setLoading]         = useState(false);
  const [open, setOpen]               = useState(false);
  const [highlight, setHighlight]     = useState(0);
  const sessionTokenRef = useRef<string>(genToken());
  const wrapRef         = useRef<HTMLDivElement>(null);
  const skipNextFetch   = useRef(false);
  const isFocused       = useRef(false);

  // Debounced fetch — only triggers when the user is actively typing (input focused)
  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (!isFocused.current) return;
    const q = value.trim();
    if (q.length < 2) {
      setPredictions([]); setOpen(false); setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await placesApi.autocomplete(q, sessionTokenRef.current);
        setPredictions(list);
        setOpen(list.length > 0);
        setHighlight(0);
      } catch {
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  // Close on click outside
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function pick(p: AutocompletePrediction) {
    setOpen(false);
    skipNextFetch.current = true;
    onChange(p.description);  // optimistic
    try {
      const detail = await placesApi.details(p.placeId, sessionTokenRef.current);
      // Token consumed → start a new session for the next edit
      sessionTokenRef.current = genToken();
      if (detail) {
        skipNextFetch.current = true;
        onChange(detail.address || p.description);
        onSelect(detail);
      }
    } catch {
      /* swallow */
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, predictions.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter')     { e.preventDefault(); pick(predictions[highlight]); }
    else if (e.key === 'Escape')    { setOpen(false); }
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        className={`input ${className ?? ''}`}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => { isFocused.current = true; predictions.length > 0 && setOpen(true); }}
        onBlur={() => { isFocused.current = false; }}
        onKeyDown={onKeyDown}
        placeholder={placeholder ?? 'Start typing an address…'}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Spinner size="sm" />
        </div>
      )}
      {open && predictions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
          {predictions.map((p, i) => (
            <button
              key={p.placeId}
              type="button"
              onMouseDown={e => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors ${
                i === highlight ? 'bg-slate-50 dark:bg-slate-800' : ''
              } hover:bg-slate-50 dark:hover:bg-slate-800`}
            >
              <Icon as={MapPin} size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{p.mainText}</p>
                {p.secondaryText && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{p.secondaryText}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
